// Precondition: ft-button component must be loaded
// Postcondition: Custom element for multi-select toggle buttons composing ft-button

interface SelectionState {
  selected: Set<string>;
}

const BUTTON_TYPE_SELECTED = "primary";
const BUTTON_TYPE_UNSELECTED = "secondary";

class MultiSelectButtons extends HTMLElement {
  private state: SelectionState = {
    selected: new Set(),
  };
  private abortController = new AbortController();

  static get observedAttributes(): string[] {
    return ["options", "selected", "size", "label"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" });
  }

  connectedCallback(): void {
    if (!customElements.get("ft-button")) {
      console.warn("ft-multi-select-buttons: ft-button must be loaded first");
    }
    this.initializeState();
    this.render();
  }

  disconnectedCallback(): void {
    this.abortController.abort();
  }

  attributeChangedCallback(
    name: string,
    oldValue: string | null,
    newValue: string | null
  ): void {
    if (oldValue !== newValue && this.shadowRoot?.children.length) {
      if (name === "selected") {
        this.initializeState();
      }
      this.render();
    }
  }

  // Precondition: None
  // Postcondition: Initializes selected state from attribute
  private initializeState(): void {
    const selectedAttr = this.getAttribute("selected");
    if (selectedAttr) {
      this.state.selected = new Set(
        selectedAttr.split(",").map((s) => s.trim())
      );
    }
  }

  // Precondition: None
  // Postcondition: Returns array of option objects from options attribute
  private getOptions(): Array<{ value: string; label: string; count?: number }> {
    const optionsAttr = this.getAttribute("options");
    if (!optionsAttr) return [];

    try {
      return JSON.parse(optionsAttr);
    } catch {
      return optionsAttr.split(",").map((s) => ({
        value: s.trim(),
        label: s.trim(),
      }));
    }
  }

  // Precondition: Shadow DOM attached
  // Postcondition: Renders component into shadow DOM and attaches event listeners
  private render(): void {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(this.getStyles());

    const template = document.createElement("template");
    template.innerHTML = this.getTemplate();

    if (this.shadowRoot) {
      this.shadowRoot.innerHTML = "";
      this.shadowRoot.adoptedStyleSheets = [sheet];
      this.shadowRoot.appendChild(template.content.cloneNode(true));
      this.setupEventListeners();
    }
  }

  // Precondition: None
  // Postcondition: Returns CSS string for shadow DOM
  private getStyles(): string {
    const size = this.getAttribute("size") || "normal";
    const gap = size === "small" ? "2px" : size === "large" ? "6px" : "4px";
    const padding = size === "small" ? "3px" : size === "large" ? "6px" : "4px";
    const countSize = size === "small" ? "14px" : size === "large" ? "20px" : "18px";
    const countFont = size === "small" ? "9px" : size === "large" ? "12px" : "11px";
    const countPad = size === "small" ? "0 3px" : size === "large" ? "0 6px" : "0 5px";

    return `
      :host {
        display: inline-block;
      }

      .container {
        display: inline-flex;
        background: var(--tab-bg, #f4f4f5);
        border-radius: var(--radius, 8px);
        padding: ${padding};
        gap: ${gap};
      }

      .count {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        min-width: ${countSize};
        height: ${countSize};
        margin-left: 4px;
        padding: ${countPad};
        background: rgba(255, 255, 255, 0.25);
        color: inherit;
        border-radius: calc(${countSize} / 2);
        font-size: ${countFont};
        font-weight: 700;
      }

      /* Unselected: muted ghost appearance */
      ft-button[type="secondary"] {
        --text: #6b7280;
        --bg: rgba(0, 0, 0, 0.03);
        --border: transparent;
      }

      ft-button[type="secondary"]:hover {
        --text: #374151;
        --bg: rgba(0, 0, 0, 0.06);
      }

      ft-button[type="secondary"] .count {
        background: rgba(107, 114, 128, 0.15);
        color: inherit;
      }

      @media (prefers-color-scheme: dark) {
        .container {
          background: var(--tab-bg, #111216);
        }

        ft-button[type="secondary"] {
          --text: #9ca3af;
          --bg: rgba(255, 255, 255, 0.04);
        }

        ft-button[type="secondary"]:hover {
          --text: #e5e7eb;
          --bg: rgba(255, 255, 255, 0.08);
        }

        ft-button[type="secondary"] .count {
          background: rgba(156, 163, 175, 0.2);
        }
      }
    `;
  }

  // Precondition: None
  // Postcondition: Returns HTML template string
  private getTemplate(): string {
    const options = this.getOptions();
    const size = this.getAttribute("size") || "normal";
    const label = this.getAttribute("label") || "Filter options";

    const buttons = options
      .map((opt) => {
        const isSelected = this.state.selected.has(opt.value);
        const type = isSelected ? BUTTON_TYPE_SELECTED : BUTTON_TYPE_UNSELECTED;
        const countHtml =
          opt.count !== undefined
            ? `<span class="count" aria-label="${opt.count} items">${opt.count}</span>`
            : "";

        return `
          <ft-button
            type="${type}"
            size="${size}"
            data-value="${opt.value}"
            aria-pressed="${isSelected}"
          >${opt.label}${countHtml}</ft-button>
        `;
      })
      .join("");

    return `<div class="container" role="group" aria-label="${label}">${buttons}</div>`;
  }

  // Precondition: Shadow DOM rendered
  // Postcondition: Event listeners attached
  private setupEventListeners(): void {
    this.abortController.abort();
    this.abortController = new AbortController();
    const signal = this.abortController.signal;

    const container = this.shadowRoot?.querySelector(".container");
    if (!container) return;

    container.addEventListener(
      "button-click",
      (e) => {
        const button = (e.target as HTMLElement).closest("ft-button");
        if (!button) return;

        const value = button.getAttribute("data-value");
        if (!value) return;

        this.toggleSelection(value);
      },
      { signal }
    );
  }

  // Precondition: value is a valid option value
  // Postcondition: Toggles selection state, updates ft-button type, dispatches event
  private toggleSelection(value: string): void {
    if (this.state.selected.has(value)) {
      this.state.selected.delete(value);
    } else {
      this.state.selected.add(value);
    }
    this.updateButtonState(value);
    this.dispatchChangeEvent();
  }

  // Precondition: value exists in options
  // Postcondition: Updates single button's type and aria-pressed without re-render
  private updateButtonState(value: string): void {
    const button = this.shadowRoot?.querySelector(
      `ft-button[data-value="${value}"]`
    );
    if (button) {
      const isSelected = this.state.selected.has(value);
      button.setAttribute(
        "type",
        isSelected ? BUTTON_TYPE_SELECTED : BUTTON_TYPE_UNSELECTED
      );
      button.setAttribute("aria-pressed", String(isSelected));
    }
  }

  // Precondition: None
  // Postcondition: Dispatches change event with current selection
  private dispatchChangeEvent(): void {
    this.dispatchEvent(
      new CustomEvent("change", {
        detail: { selected: Array.from(this.state.selected) },
        bubbles: true,
        composed: true,
      })
    );
  }

  // Public API

  // Precondition: None
  // Postcondition: Returns array of currently selected values
  getSelected(): string[] {
    return Array.from(this.state.selected);
  }

  // Precondition: values is an array of strings
  // Postcondition: Sets selected values and updates UI without full re-render
  setSelected(values: string[]): void {
    const options = this.getOptions();
    this.state.selected = new Set(values);
    options.forEach((opt) => this.updateButtonState(opt.value));
  }

  // Precondition: None
  // Postcondition: Clears all selections without full re-render
  clearSelection(): void {
    const options = this.getOptions();
    this.state.selected.clear();
    options.forEach((opt) => this.updateButtonState(opt.value));
  }

  // Precondition: None
  // Postcondition: Selects all options without full re-render
  selectAll(): void {
    const options = this.getOptions();
    this.state.selected = new Set(options.map((o) => o.value));
    options.forEach((opt) => this.updateButtonState(opt.value));
  }

  // Precondition: value is a string
  // Postcondition: Returns true if value is selected
  isSelected(value: string): boolean {
    return this.state.selected.has(value);
  }
}

// Register custom element
if (!customElements.get("ft-multi-select-buttons")) {
  customElements.define("ft-multi-select-buttons", MultiSelectButtons);
}
